"use client";
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import CategoryProCard from "@/components/CategoryProCard";
import ProViewBeacon from "@/components/ProViewBeacon";
import type { ExpertItem } from "@/components/CategoryExpertsList";
import { popularCities, findCity } from "@/lib/israelCities";
import { suggestCities } from "@/lib/searchText";
import { cityPageOrder, proCityDistanceNote } from "@/lib/proMatching";
import { cleanCity } from "@/lib/city";

// Article pros block, client part (Founder 15/10/26): the pro cards sit directly on the page (no outer card), plus a city drop-down
// (icon + chosen city name). Reuses the category page pipeline: the SAME city list (lib/israelCities), the SAME order/radius ladder
// (lib/proMatching cityPageOrder) and the SAME CategoryProCard. No device geolocation. Empty result -> one short line + the national list.
const SHOWN = 3;
const PinIcon = () => (
  <svg aria-hidden="true" viewBox="0 0 512 512" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="32" strokeLinecap="round" strokeLinejoin="round">
    <path d="M256 48c-79.5 0-144 61.39-144 137 0 87 96 224.87 131.25 272.49a15.77 15.77 0 0025.5 0C304 409.89 400 272 400 185c0-75.61-64.5-137-144-137z" />
    <circle cx="256" cy="192" r="48" />
  </svg>
);

export default function ArticleProList({ experts, availStatus, nationwide, variant }: {
  experts: ExpertItem[]; availStatus: "today" | "tomorrow" | "week"; nationwide: boolean; variant: string;
}) {
  const [city, setCity] = useState<{ name: string; lat: number; lng: number } | null>(null);
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const wrapRef = useRef<HTMLDivElement>(null);
  const btnRef = useRef<HTMLButtonElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [pos, setPos] = useState<{ up: boolean; listMax: number }>({ up: false, listMax: 260 });

  // 1015art2: open downward unless the space below (viewport minus the fixed bottom dock) is smaller than above; cap the list to free space.
  useLayoutEffect(() => {
    if (!open || !btnRef.current) return;
    try { btnRef.current.scrollIntoView({ block: "nearest", behavior: "instant" as ScrollBehavior }); } catch { /* old browser */ }
    const measure = () => {
      const b = btnRef.current; if (!b) return;
      const r = b.getBoundingClientRect();
      const dock = document.querySelector(".ct2-dock") as HTMLElement | null;
      const dr = dock ? dock.getBoundingClientRect() : null;
      const bottomLimit = dr && dr.height > 0 && dr.top < window.innerHeight ? dr.top : window.innerHeight;
      const below = bottomLimit - r.bottom - 6 - 12;
      const above = r.top - 6 - 12 - 64; // 64 = sticky header allowance
      const up = below < 230 && above > below;
      const free = up ? above : below;
      setPos({ up, listMax: Math.max(96, Math.min(260, free - 8 * 2 - 44 - 6)) });
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    inputRef.current?.focus();
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") { setOpen(false); btnRef.current?.focus(); } };
    const onDown = (e: MouseEvent | TouchEvent) => { if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onDown);
    document.addEventListener("touchstart", onDown);
    return () => { document.removeEventListener("keydown", onKey); document.removeEventListener("mousedown", onDown); document.removeEventListener("touchstart", onDown); };
  }, [open]);

  const options = useMemo(() => (q.trim() ? suggestCities(q, 30) : popularCities(30)), [q]);

  const { list, empty } = useMemo(() => {
    if (!city) return { list: experts.slice(0, SHOWN), empty: false };
    const r = cityPageOrder(experts as any[], city.name, { lat: city.lat, lng: city.lng }, { nationwide });
    const l = (r.list as ExpertItem[]).slice(0, SHOWN);
    return l.length ? { list: l, empty: false } : { list: experts.slice(0, SHOWN), empty: true };
  }, [city, experts, nationwide]);

  function pick(name: string | null) {
    if (!name) setCity(null);
    else { const c = findCity(name); setCity(c ? { name: c.name, lat: c.lat, lng: c.lng } : null); }
    setOpen(false); setQ("");
    btnRef.current?.focus();
  }
  const cityClean = city ? cleanCity(city.name) : "";

  return (
    <div data-article-pros-list={variant}>
      <div ref={wrapRef} className="relative mb-4" style={{ display: "inline-block" }}>
        <button ref={btnRef} type="button" aria-haspopup="listbox" aria-expanded={open} data-article-city-btn
          onClick={() => setOpen(o => !o)}
          className="inline-flex items-center gap-2 rounded-full px-4 font-bold"
          style={{ minHeight: 44, background: "#eff6ff", color: "#0d47a1", border: "1.5px solid #bfdbfe", cursor: "pointer", fontSize: 15, fontFamily: "inherit" }}>
          <PinIcon /><span>{city ? city.name : "בחירת עיר"}</span>
          <svg aria-hidden="true" viewBox="0 0 512 512" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="48" strokeLinecap="round" strokeLinejoin="round"><path d="M112 184l144 144 144-144" /></svg>
        </button>
        {open && (
          <div role="dialog" aria-label="בחירת עיר" className="rounded-2xl bg-white" data-article-city-menu
            style={{ position: "absolute", insetInlineStart: 0, ...(pos.up ? { bottom: "calc(100% + 6px)" } : { top: "calc(100% + 6px)" }), zIndex: 40, width: "min(320px, calc(100vw - 32px))", border: "1px solid #cbd5e1", boxShadow: "0 12px 32px rgba(15,23,42,.18)", padding: 8 }}>
            <input ref={inputRef} value={q} onChange={e => setQ(e.target.value)} placeholder="חיפוש עיר..." aria-label="חיפוש עיר"
              onKeyDown={e => { if (e.key === "Enter" && options[0]) { e.preventDefault(); pick(options[0]); } }}
              style={{ width: "100%", minHeight: 44, border: "1.5px solid #bfdbfe", borderRadius: 12, padding: "0 12px", fontSize: 16, fontFamily: "inherit", color: "#0f172a", outline: "none", boxSizing: "border-box" }} />
            <ul role="listbox" aria-label="ערים" style={{ listStyle: "none", margin: "6px 0 0", padding: 0, maxHeight: pos.listMax, overflowY: "auto" }}>
              {!q.trim() && (
                <li role="option" aria-selected={!city}>
                  <button type="button" onClick={() => pick(null)} style={{ width: "100%", minHeight: 44, textAlign: "start", padding: "0 12px", background: !city ? "#eff6ff" : "none", border: "none", borderRadius: 10, cursor: "pointer", fontSize: 15, fontWeight: 700, color: "#64748b", fontFamily: "inherit" }}>כל הארץ</button>
                </li>
              )}
              {options.map(c => (
                <li key={c} role="option" aria-selected={city?.name === c}>
                  <button type="button" onClick={() => pick(c)}
                    style={{ width: "100%", minHeight: 44, textAlign: "start", padding: "0 12px", background: city?.name === c ? "#eff6ff" : "none", border: "none", borderRadius: 10, cursor: "pointer", fontSize: 15, fontWeight: 600, color: "#0f172a", fontFamily: "inherit" }}>{c}</button>
                </li>
              ))}
              {!options.length && <li style={{ padding: "10px 12px", color: "#64748b", fontSize: 14 }}>לא נמצאה עיר</li>}
            </ul>
          </div>
        )}
      </div>
      {empty && <p className="mb-3 text-sm font-bold" style={{ color: "#475569" }} role="status">אין עדיין מומחים ב{cityClean} — הנה המומחים הקרובים</p>}
      <div className="space-y-4" aria-live="polite">
        {list.map((e, i) => (
          <div key={e.id}>
            <ProViewBeacon id={e.id} name={e.firstName} categorySlug={e.categorySlug} />
            <CategoryProCard proArea={null} priority={false}
              id={e.id} href={e.href} firstName={e.firstName} fullName={e.fullName} phone={e.phone} whatsappValid={e.whatsappValid}
              imgSrc={e.imgSrc} rating10={e.rating10} reviewCount={e.reviewCount}
              categorySlug={e.categorySlug} categoryName={e.categoryName}
              locationStr={e.locationStr} availStatus={availStatus} avail={e.avail} reviews={e.reviews as any}
              nationwide={nationwide} proCity={e.proCity}
              distanceNote={!nationwide && city && !empty && cityClean ? proCityDistanceNote(e as any, cityClean, { lat: city.lat, lng: city.lng }) : null} />
          </div>
        ))}
      </div>
    </div>
  );
}
